'use client';

import { useEffect, useId, useRef, useState, useSyncExternalStore, type ComponentProps, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { Button } from './Button';
import { useNavigationPrompt } from './NavigationBlocker';
import { useTranslation } from '../lib/i18n/TranslationContext';
import { OctagonAlert } from 'lucide-react';

const botaoBarra = 'gap-1.5 !text-xs rounded-lg !px-3 !py-1.5';

export function ActionBarButton({ className, ...props }: ComponentProps<typeof Button>) {
	return <Button variant='ghost' {...props} className={twMerge(botaoBarra, 'text-white hover:bg-white/10', className)} />;
}

export type ActionBarProps = {
	open: boolean;
	onSave: (proceed: (redirect?: boolean) => void) => void | Promise<void>;
	onCancel?: () => void;
	message?: string;
	savingLabel?: string;
	className?: string;
	children?: ReactNode;
	ignorarIds?: string[];
};

let config: ActionBarProps | null = null;
const registros = new Map<string, ActionBarProps>();
const listeners = new Set<() => void>();

const recalcular = () => {
	const todas = Array.from(registros.values());
	config = todas.find((barra) => barra.open === true) ?? todas[todas.length - 1] ?? null;
};

const subscribe = (l: () => void) => {
	listeners.add(l);
	return () => {
		listeners.delete(l);
	};
};
const notify = () => listeners.forEach((l) => l());
const getConfig = () => config;
const getServerConfig = (): ActionBarProps | null => null;

export function ActionBar(props: ActionBarProps) {
	const id = useId();

	// sem array de dependências: os callbacks e o children mudam de identidade a cada render
	useEffect(() => {
		registros.set(id, props);
		recalcular();
		notify();
	});

	useEffect(() => {
		return () => {
			registros.delete(id);
			recalcular();
			notify();
		};
	}, [id]);

	return null;
}

export function podeNavegarPara(id: string) {
	if (config?.open !== true) return true;
	return (config.ignorarIds ?? []).includes(id);
}

export const actionBarAberta = () => config?.open === true;
const getServerAberta = () => false;

export function useActionBarAberta() {
	return useSyncExternalStore(subscribe, actionBarAberta, getServerAberta);
}

export function ActionBarSlot() {
	const { t } = useTranslation();
	const cfg = useSyncExternalStore(subscribe, getConfig, getServerConfig);
	const aberta = cfg?.open === true;
	const reduzirMovimento = useReducedMotion();

	const { pending, attempts, takeNavigation } = useNavigationPrompt(aberta);
	const [tremendo, setTremendo] = useState(false);
	const [salvando, setSalvando] = useState(false);

	const ultimaTentativa = useRef(attempts);
	useEffect(() => {
		if (attempts === ultimaTentativa.current) return;
		ultimaTentativa.current = attempts;
		setTremendo(true);
	}, [attempts]);

	useEffect(() => {
		if (!pending || !aberta) setTremendo(false);
	}, [pending, aberta]);

	const salvar = async () => {
		if (salvando) return;
		const seguir = takeNavigation();
		const proceed = (redirect = true) => {
			if (redirect) seguir();
		};

		try {
			setSalvando(true);
			await cfg?.onSave(proceed);
		} finally {
			setSalvando(false);
		}
	};

	const cancelar = () => {
		takeNavigation();
		cfg?.onCancel?.();
	};

	const salvarRef = useRef(salvar);
	salvarRef.current = salvar;

	useEffect(() => {
		if (!aberta) return;

		const atalho = (e: KeyboardEvent) => {
			if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 's') return;

			e.preventDefault();
			salvarRef.current();
		};

		window.addEventListener('keydown', atalho);

		return () => window.removeEventListener('keydown', atalho);
	}, [aberta]);

	return (
		<AnimatePresence initial={false}>
			{cfg && aberta ? (
				<motion.div
					key='empreender-action-bar'
					className='shrink-0 overflow-hidden bg-white px-4'
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: 'auto', opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: reduzirMovimento ? 0 : 0.2, ease: 'easeOut' }}>
					<div className='mx-auto w-full max-w-[600px] pb-2 pt-1'>
						<div
							role='status'
							aria-live='polite'
							onAnimationEnd={(e) => {
								if (e.currentTarget === e.target) setTremendo(false);
							}}
							className={twMerge(
								'flex flex-row items-center gap-1 !py-1 !pl-2 !pr-1 w-full rounded-lg shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1)] transition-colors',
								tremendo ? 'bg-[#641723]' : 'bg-gray-900',
								tremendo && 'empreender-shake',
								cfg.className
							)}>
							<div className='flex flex-row items-center gap-2 flex-1 min-w-0 text-white text-xs font-medium leading-6'>
								<OctagonAlert size={16} />
								<p className='flex-1 min-w-0 truncate'>{cfg.message ?? t('Você tem alterações que não foram salvas!')}</p>
							</div>
							{cfg.children}

							<Button onClick={cancelar} disabled={salvando} className={twMerge(botaoBarra, 'bg-white/10 text-white hover:bg-white/20')}>
								{t('Cancelar alterações')}
							</Button>
							<Button variant='secondary' onClick={salvar} disabled={salvando} className={botaoBarra}>
								{salvando ? cfg.savingLabel ?? t('Salvando...') : t('Salvar')}
							</Button>
						</div>
					</div>
				</motion.div>
			) : null}
		</AnimatePresence>
	);
}
